import React, { useState, useEffect } from 'react';

import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addHours, isBefore, isAfter, startOfDay, endOfDay } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { formatTime12Hour } from '../utils/timeFormat';

const BookingForm = ({ gym, onBookingComplete }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [personalTraining, setPersonalTraining] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const navigate = useNavigate();

  // Check payment method status on component mount
  useEffect(() => {
    checkPaymentMethod();
  }, []);

  // Re-check payment method when component comes back into focus
  useEffect(() => {
    const handleFocus = () => {
      checkPaymentMethod();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const checkPaymentMethod = async () => {
    try {
      setCheckingPayment(true);
      const response = await axios.get('/api/payment-info');
      setHasPaymentMethod(response.data.hasPaymentMethod);
      setPaymentDetails(response.data);
    } catch (error) {
      console.error('Error checking payment method:', error);
      setHasPaymentMethod(false);
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setStartTime(null);
    setEndTime(null);
  };

  const combineDateAndTime = (date, time) => {
    if (!time) return null;
    const newDate = new Date(date);
    newDate.setHours(time.getHours());
    newDate.setMinutes(time.getMinutes());
    return newDate;
  };

  const handleStartTimeChange = (newTime) => {
    const combinedTime = combineDateAndTime(selectedDate, newTime);
    setStartTime(combinedTime);
    // Set end time to 1 hour after start time by default
    if (newTime) {
      setEndTime(addHours(combinedTime, 1));
    } else {
      setEndTime(null);
    }
  };

  const handleEndTimeChange = (newTime) => {
    const combinedTime = combineDateAndTime(selectedDate, newTime);
    setEndTime(combinedTime);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      if (!startTime || !endTime) {
        setError('Please select both start and end times');
        return;
      }

      // Validate times
      if (isBefore(endTime, startTime)) {
        setError('End time must be after start time');
        return;
      }

      if (isBefore(startTime, startOfDay(selectedDate)) || isAfter(endTime, endOfDay(selectedDate))) {
        setError('Booking must be within the selected date');
        return;
      }

      const bookingData = {
        gymId: gym._id,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        personalTraining: personalTraining
      };

      const response = await axios.post('/api/bookings', bookingData);
      setSuccess(true);
      
      if (onBookingComplete) {
        onBookingComplete(response.data);
      }
    } catch (err) {
      console.error('Booking error:', err);
      if (err.response?.status === 409) {
        setError('This time slot is already booked. Please choose a different time.');
      } else if (err.response?.data?.requiresPaymentMethod) {
        if (err.response?.data?.incompletePaymentMethod) {
          setError('Your payment method information is incomplete. Please re-add your payment method in the payment settings.');
        } else {
          setError('Please add a payment method before booking. You can add one in your account settings.');
        }
      } else if (err.response?.data?.paymentError) {
        // Show specific payment error details
        const paymentError = err.response.data;
        let errorMessage = 'Payment failed. ';
        
        if (paymentError.errorCode) {
          errorMessage += `Error: ${paymentError.errorCode}. `;
        }
        if (paymentError.declineCode) {
          errorMessage += `Reason: ${paymentError.declineCode}. `;
        }
        errorMessage += paymentError.paymentError || 'Please check your payment method.';
        
        setError(errorMessage);
      } else {
        setError(err.response?.data?.message || 'Failed to create booking');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking payment method
  if (checkingPayment) {
    return (
      <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Checking payment method...
        </Typography>
      </Paper>
    );
  }

  // Show payment method required message
  if (!hasPaymentMethod) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Book This Gym
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Payment method required
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You need to add a payment method before you can book this gym.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/payment')}
            sx={{ mt: 1 }}
          >
            Add Payment Method
          </Button>
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Book This Gym
      </Typography>

      {/* Payment Method Status */}
      <Alert severity="success" sx={{ mb: 2 }}>
        <Typography variant="body2" gutterBottom>
          ✓ Payment method ready for booking
        </Typography>
        {paymentDetails && (
          <Typography variant="body2" color="text.secondary">
            {paymentDetails.cardBrand} •••• {paymentDetails.lastFourDigits} 
            (expires {paymentDetails.expiryMonth}/{paymentDetails.expiryYear})
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
          Note: Test cards may show as declined but will be processed for demo purposes.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          {error.includes('payment method') && (
            <Box sx={{ mt: 1 }}>
              <Link 
                component="button" 
                variant="body2" 
                onClick={() => navigate('/payment')}
                sx={{ textDecoration: 'none' }}
              >
                {error.includes('incomplete') ? 'Re-add Payment Method' : 'Add Payment Method'}
              </Link>
            </Box>
          )}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Booking created successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={selectedDate}
              onChange={handleDateChange}
              minDate={new Date()}
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Amenities Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Available Amenities
          </Typography>
          <Grid container spacing={1}>
            {gym.amenities && Object.entries(gym.amenities).map(([key, value]) => {
              // Convert value to boolean if it's a string
              const boolValue = typeof value === 'string' ? value === 'true' : Boolean(value);
              return boolValue && (
                <Grid item xs={6} sm={4} key={key}>
                  <Typography variant="body2" color="text.secondary">
                    ✓ {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select Time
            </Typography>
            
            {/* Available Hours Display */}
            {gym.availableHours && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Available Hours for {format(selectedDate, 'EEEE')}:
                </Typography>
                {(() => {
                  const dayName = format(selectedDate, 'EEEE').toLowerCase();
                  const daySchedule = gym.availableHours[dayName];
                  if (daySchedule && daySchedule.enabled) {
                    return (
                      <Typography variant="body2" color="success.main">
                        {formatTime12Hour(daySchedule.start)} - {formatTime12Hour(daySchedule.end)}
                      </Typography>
                    );
                  } else {
                    return (
                      <Typography variant="body2" color="error.main">
                        Closed on {format(selectedDate, 'EEEE')}s
                      </Typography>
                    );
                  }
                })()}
              </Box>
            )}
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TimePicker
                    label="Start Time"
                    value={startTime}
                    onChange={handleStartTimeChange}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TimePicker
                    label="End Time"
                    value={endTime}
                    onChange={handleEndTimeChange}
                    minTime={startTime}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Booking Summary
              </Typography>
              {startTime && endTime && (
                <Typography variant="body2" color="text.secondary">
                  {format(startTime, 'MMM d, yyyy h:mm a')} - {format(endTime, 'h:mm a')}
                </Typography>
              )}
              
              {/* Personal Training Option */}
              {(() => {
                const personalTrainingEnabled = typeof gym.amenities?.personalTraining === 'string' 
                  ? gym.amenities.personalTraining === 'true' 
                  : Boolean(gym.amenities?.personalTraining);
                return personalTrainingEnabled;
              })() && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Personal Training Options
                  </Typography>
                  <Typography variant="body2" color="success.main" gutterBottom>
                    Premium: +${gym.personalTrainingPremium || 0}/hr
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={personalTraining}
                        onChange={(e) => setPersonalTraining(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Include Personal Training"
                  />
                </Box>
              )}
              
              {/* Price Breakdown */}
              {startTime && endTime && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Price Breakdown
                  </Typography>
                                     {(() => {
                     const hours = (endTime - startTime) / (1000 * 60 * 60);
                     const basePrice = gym.pricePerHour * hours;
                     const trainingPremium = personalTraining ? (gym.personalTrainingPremium || 0) * hours : 0;
                     const total = basePrice + trainingPremium;
                     

                     
                     return (
                       <>
                         <Typography variant="body2" color="text.secondary">
                           Base Rate: ${gym.pricePerHour}/hr × {hours.toFixed(1)}hr = ${basePrice.toFixed(2)}
                         </Typography>
                         {personalTraining && (
                           <Typography variant="body2" color="success.main">
                             Personal Training: +${gym.personalTrainingPremium || 0}/hr × {hours.toFixed(1)}hr = +${trainingPremium.toFixed(2)}
                           </Typography>
                         )}
                         <Typography variant="h6" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                           Total: ${total.toFixed(2)}
                         </Typography>
                       </>
                     );
                   })()}
                </Box>
              )}
            </Box>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={handleSubmit}
              disabled={loading || !startTime || !endTime}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Confirm Booking'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BookingForm; 